import React, { memo, useCallback, useMemo, useState } from "react";

import { Button } from "@swiggy-private/dls-web-core/dist/components/Button";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as CloseIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/clearLight/24";
import { BottomSheet, isEmpty, useEffectOnce } from "@swiggy-private/core-ui";
import OptionItem from "./components/OptionItem";

import { getPlanForAnalytics, isSwiggyOnePlanVariants } from "@cart-ui/helpers/OfferBundle";

// types
import { IOfferBundleHalfCardProps } from "./types";
import { ButtonTitles, Content, SelectedOption } from "@swiggy-private/cart-types";

// constants
import { OBJECT_VALUES } from "@cart-ui/constants/OfferBundle";
import { OFFER_BUNDLE } from "@swiggy-private/cart-test-ids";

// styles
import styles from "./styles/OfferBundleHalfCard.scss";

const OfferBundleHalfCard = ({
    item,
    onClose,
    onHalfCardConfirm,
    planDetails,
    analyticsEvents,
    isM2CFlow,
}: IOfferBundleHalfCardProps) => {
    const [selectedOption, setSelectedOption] = useState(SelectedOption.ADD);
    const isOneProgram = useMemo(() => planDetails && isSwiggyOnePlanVariants(planDetails), [planDetails]);
    const onePlanType = useMemo(() => (planDetails && isOneProgram ? planDetails.type : undefined), [
        isOneProgram,
        planDetails,
    ]);

    useEffectOnce(() => {
        analyticsEvents.impressionHalfCard(isM2CFlow, onePlanType);
    });

    const onCloseEvent = useCallback(() => {
        analyticsEvents.clickHalfCardConfirm(
            getPlanForAnalytics(selectedOption, planDetails, onePlanType),
            OBJECT_VALUES.DISMISS,
            isM2CFlow,
        );
        onClose();
    }, [onClose, analyticsEvents, planDetails, isM2CFlow, selectedOption, onePlanType]);

    const onHalfCardConfirmEvent = useCallback(
        (selectedOption: number) => {
            analyticsEvents.clickHalfCardConfirm(
                getPlanForAnalytics(selectedOption, planDetails, onePlanType),
                OBJECT_VALUES.CONFIRM,
                isM2CFlow,
            );
            onHalfCardConfirm(selectedOption);
        },
        [onHalfCardConfirm, analyticsEvents, planDetails, isM2CFlow, onePlanType],
    );

    const closeIcon = useMemo(
        () => <GenericIcon Icon={CloseIcon} onClick={onCloseEvent} className={styles.closeButton} />,
        [onCloseEvent],
    );

    const title = useMemo(() => (typeof item?.title === "string" ? item?.title : item?.title?.text), [item?.title]);

    if (!item) return null;

    return (
        <BottomSheet onClose={onCloseEvent} closeIcon={closeIcon} containerDataTestId={OFFER_BUNDLE.HALF_CARD}>
            {(): React.ReactElement => (
                <div className={styles.offerBundleHalfCardContainer}>
                    {!isEmpty(title) && (
                        <Text variant="V2.heading2.neutral.black" color="text_Highest_Emphasis">
                            {title}
                        </Text>
                    )}
                    <div className={styles.optionList}>
                        {item?.content?.map((option: Content, index: number) => {
                            return (
                                <OptionItem
                                    key={index}
                                    {...option}
                                    isSelected={index === selectedOption}
                                    onSelect={() => setSelectedOption(index)}
                                />
                            );
                        })}
                    </div>
                    <Button
                        variant="primary"
                        onClick={() => onHalfCardConfirmEvent(selectedOption)}
                        data-testid={OFFER_BUNDLE.HALF_CARD_CTA}
                    >
                        {ButtonTitles.CONFIRM}
                    </Button>
                </div>
            )}
        </BottomSheet>
    );
};

export default memo(OfferBundleHalfCard);
